import React, { useState, useRef } from 'react';
import { Image, Type, Palette, Layout, X, Upload } from '../icons';
import { uploadCardPhoto } from '../services/api';
import mediaUrl from '../utils/mediaUrl';

const TEMPLATES = [
  {
    id: 'classic',
    name: 'Classic Elegance',
    preview: 'linear-gradient(135deg, #fdf2f8, #fff)',
    defaults: { primaryColor: '#be185d', fontFamily: 'Georgia, serif', heading: '', subheading: 'REQUEST THE PLEASURE OF YOUR COMPANY', bodyText: '', footerText: 'We look forward to celebrating with you!' },
  },
  {
    id: 'modern',
    name: 'Modern Minimal',
    preview: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
    defaults: { primaryColor: '#1e293b', fontFamily: "'Segoe UI', sans-serif", heading: '', subheading: 'YOU ARE CORDIALLY INVITED', bodyText: '', footerText: 'Save the date!' },
  },
  {
    id: 'floral',
    name: 'Floral Garden',
    preview: 'linear-gradient(135deg, #fef3c7, #fde68a)',
    defaults: { primaryColor: '#92400e', fontFamily: "'Segoe UI', sans-serif", heading: '', subheading: 'TOGETHER WITH THEIR FAMILIES', bodyText: '', footerText: 'Dinner and dancing to follow' },
  },
  {
    id: 'royal',
    name: 'Royal Nepali',
    preview: 'linear-gradient(135deg, #7c2d12, #dc2626)',
    defaults: { primaryColor: '#dc2626', fontFamily: "Georgia, serif", heading: '', subheading: 'शुभ विवाह', bodyText: '', footerText: 'सादर निमन्त्रणा' },
  },
  {
    id: 'mountain',
    name: 'Himalayan Bliss',
    preview: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    defaults: { primaryColor: '#1d4ed8', fontFamily: "'Segoe UI', sans-serif", heading: '', subheading: 'JOIN US IN THE MOUNTAINS', bodyText: '', footerText: 'An adventure begins...' },
  },
  {
    id: 'sunset',
    name: 'Golden Sunset',
    preview: 'linear-gradient(135deg, #fecaca, #fde68a)',
    defaults: { primaryColor: '#b91c1c', fontFamily: "Georgia, serif", heading: '', subheading: 'TWO HEARTS, ONE LOVE', bodyText: '', footerText: 'As the sun sets on a new beginning' },
  },
];

const TEMPLATE_STYLES = {
  classic: (d) => ({
    wrapper: { background: '#fdf2f8', borderRadius: '16px', overflow: 'hidden', fontFamily: d.fontFamily },
    header: { background: `linear-gradient(135deg, ${d.primaryColor}, ${d.primaryColor}dd)`, padding: '40px 30px', textAlign: 'center', color: '#fff' },
    body: { padding: '30px', textAlign: 'center' },
    footer: { background: '#fce7f3', padding: '16px', textAlign: 'center' },
  }),
  modern: (d) => ({
    wrapper: { background: '#fff', borderRadius: '4px', overflow: 'hidden', fontFamily: d.fontFamily, border: `2px solid ${d.primaryColor}` },
    header: { padding: '40px 30px', textAlign: 'center', borderBottom: `1px solid ${d.primaryColor}22` },
    body: { padding: '30px', textAlign: 'center' },
    footer: { padding: '16px', textAlign: 'center', borderTop: `1px solid ${d.primaryColor}22` },
  }),
  floral: (d) => ({
    wrapper: { background: 'linear-gradient(135deg, #fefce8, #fff)', borderRadius: '16px', overflow: 'hidden', fontFamily: d.fontFamily, border: '2px solid #d97706' },
    header: { padding: '40px 30px', textAlign: 'center', background: `linear-gradient(135deg, ${d.primaryColor}11, ${d.primaryColor}05)` },
    body: { padding: '30px', textAlign: 'center' },
    footer: { background: '#fef3c7', padding: '16px', textAlign: 'center' },
  }),
  royal: (d) => ({
    wrapper: { background: 'linear-gradient(180deg, #fef2f2, #fff)', borderRadius: '16px', overflow: 'hidden', fontFamily: d.fontFamily, border: '3px double #dc2626' },
    header: { background: `linear-gradient(135deg, ${d.primaryColor}, #991b1b)`, padding: '40px 30px', textAlign: 'center', color: '#fff' },
    body: { padding: '30px', textAlign: 'center' },
    footer: { background: '#fef2f2', padding: '16px', textAlign: 'center' },
  }),
  mountain: (d) => ({
    wrapper: { background: 'linear-gradient(180deg, #dbeafe, #fff)', borderRadius: '16px', overflow: 'hidden', fontFamily: d.fontFamily },
    header: { background: `linear-gradient(135deg, ${d.primaryColor}, #3b82f6)`, padding: '40px 30px', textAlign: 'center', color: '#fff' },
    body: { padding: '30px', textAlign: 'center' },
    footer: { background: '#dbeafe', padding: '16px', textAlign: 'center' },
  }),
  sunset: (d) => ({
    wrapper: { background: 'linear-gradient(180deg, #fff1f2, #fef9c3, #fff)', borderRadius: '16px', overflow: 'hidden', fontFamily: d.fontFamily },
    header: { background: `linear-gradient(135deg, ${d.primaryColor}, #ea580c)`, padding: '40px 30px', textAlign: 'center', color: '#fff' },
    body: { padding: '30px', textAlign: 'center' },
    footer: { background: '#fef2f2', padding: '16px', textAlign: 'center' },
  }),
};

function CardPreview({ template, data, coupleNames, weddingDate, venue }) {
  const d = { ...TEMPLATES.find(t => t.id === template)?.defaults, ...data };
  const styles = (TEMPLATE_STYLES[template] || TEMPLATE_STYLES.classic)(d);
  const heading = d.heading || coupleNames || 'Your Names';
  const dateStr = weddingDate ? new Date(weddingDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Date TBD';
  const hasHeaderBg = ['classic', 'royal', 'mountain', 'sunset'].includes(template);

  return (
    <div style={{ ...styles.wrapper, maxWidth: '400px', margin: '0 auto', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
      {d.photoUrl && (
        <div style={{ height: '180px', overflow: 'hidden' }}>
          <img src={mediaUrl(d.photoUrl)} alt="Card" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <div style={styles.header}>
        <p style={{ fontSize: '11px', letterSpacing: '2px', margin: '0 0 8px', opacity: 0.85, color: hasHeaderBg ? '#fff' : d.primaryColor }}>{d.subheading}</p>
        <h2 style={{ margin: 0, fontSize: '26px', fontWeight: 600, color: hasHeaderBg ? '#fff' : d.primaryColor }}>{heading}</h2>
      </div>
      <div style={styles.body}>
        <p style={{ color: '#6b7280', fontSize: '13px', margin: '0 0 16px' }}>{d.bodyText || 'Your personal message here'}</p>
        <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '16px', margin: '0 auto', maxWidth: '280px' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase' }}>When</div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937', marginBottom: '8px' }}>{dateStr}</div>
          <div style={{ fontSize: '12px', color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase' }}>Where</div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#1f2937' }}>{venue || 'Venue TBD'}</div>
        </div>
        <div style={{ marginTop: '20px' }}>
          <span style={{ display: 'inline-block', background: d.primaryColor, color: '#fff', padding: '10px 28px', borderRadius: '8px', fontWeight: 600, fontSize: '14px' }}>RSVP</span>
        </div>
      </div>
      <div style={styles.footer}>
        <p style={{ margin: 0, fontSize: '12px', color: d.primaryColor, fontStyle: 'italic' }}>{d.footerText}</p>
      </div>
    </div>
  );
}

function InvitationCardEditor({ template: initialTemplate, cardData: initialData, coupleNames, weddingDate, venue, onSave, onCancel }) {
  const [template, setTemplate] = useState(initialTemplate || 'classic');
  const [data, setData] = useState(initialData || {});
  const [activePanel, setActivePanel] = useState('template');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const currentDefaults = TEMPLATES.find(t => t.id === template)?.defaults || {};
  const merged = { ...currentDefaults, ...data };

  const updateData = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so re-picking the same file after an error still fires onChange
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('That photo is larger than 10MB. Please choose a smaller one.'); return; }
    setUploading(true);
    try {
      const res = await uploadCardPhoto(file);
      updateData('photoUrl', res.data.url);
    } catch (err) {
      // The server now answers with the real reason (wrong type, too large)
      alert(err.response?.data?.error || 'Failed to upload photo');
    }
    setUploading(false);
  };

  const handleTemplateChange = (id) => {
    setTemplate(id);
    // Reset custom colors/fonts to template defaults when switching
    const newDefaults = TEMPLATES.find(t => t.id === id)?.defaults || {};
    setData(prev => ({
      ...prev,
      primaryColor: newDefaults.primaryColor,
      fontFamily: newDefaults.fontFamily,
      subheading: prev.subheading || newDefaults.subheading,
      footerText: prev.footerText || newDefaults.footerText,
    }));
  };

  return (
    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
      {/* Left: Editor panels */}
      <div style={{ flex: '1 1 320px', minWidth: '280px' }}>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[
            { key: 'template', icon: <Layout size={14} />, label: 'Template' },
            { key: 'text', icon: <Type size={14} />, label: 'Text' },
            { key: 'photo', icon: <Image size={14} />, label: 'Photo' },
            { key: 'style', icon: <Palette size={14} />, label: 'Style' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActivePanel(tab.key)}
              style={{
                padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                border: activePanel === tab.key ? '2px solid #be185d' : '1px solid #d1d5db',
                background: activePanel === tab.key ? '#fdf2f8' : '#fff',
                color: activePanel === tab.key ? '#be185d' : '#6b7280',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {activePanel === 'template' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {TEMPLATES.map(t => (
              <div key={t.id} onClick={() => handleTemplateChange(t.id)}
                style={{
                  padding: '16px', borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                  border: template === t.id ? '2px solid #be185d' : '2px solid #e5e7eb',
                  background: t.preview,
                  transition: 'all 0.2s',
                }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{t.name}</div>
              </div>
            ))}
          </div>
        )}

        {activePanel === 'text' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Heading (Couple Names)</label>
              <input type="text" value={merged.heading || ''} onChange={e => updateData('heading', e.target.value)}
                placeholder={coupleNames || 'Your Names'}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Subheading</label>
              <input type="text" value={merged.subheading || ''} onChange={e => updateData('subheading', e.target.value)}
                placeholder="REQUEST THE PLEASURE OF YOUR COMPANY"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Body Text / Personal Message</label>
              <textarea value={merged.bodyText || ''} onChange={e => updateData('bodyText', e.target.value)}
                placeholder="Your personal message here..."
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', minHeight: '80px', resize: 'vertical' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Footer Text</label>
              <input type="text" value={merged.footerText || ''} onChange={e => updateData('footerText', e.target.value)}
                placeholder="We look forward to celebrating with you!"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
            </div>
          </div>
        )}

        {activePanel === 'photo' && (
          <div style={{ textAlign: 'center' }}>
            {merged.photoUrl ? (
              <div style={{ position: 'relative', display: 'inline-block', marginBottom: '12px' }}>
                <img src={mediaUrl(merged.photoUrl)} alt="Card" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '12px', objectFit: 'cover' }} />
                <button onClick={() => updateData('photoUrl', '')}
                  style={{ position: 'absolute', top: '8px', right: '8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div onClick={() => fileInputRef.current?.click()}
                style={{ padding: '40px', border: '2px dashed #d1d5db', borderRadius: '12px', cursor: 'pointer', color: '#6b7280', marginBottom: '12px' }}>
                <Upload size={32} style={{ marginBottom: '8px' }} />
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{uploading ? 'Uploading...' : 'Click to upload a photo'}</div>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>JPG, PNG or WebP, max 10MB</div>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} style={{ display: 'none' }} />
            {!merged.photoUrl && (
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-outline" disabled={uploading}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto' }}>
                <Upload size={14} /> {uploading ? 'Uploading...' : 'Choose Photo'}
              </button>
            )}
          </div>
        )}

        {activePanel === 'style' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Primary Color</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['#be185d', '#dc2626', '#ea580c', '#d97706', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#1e293b'].map(color => (
                  <div key={color} onClick={() => updateData('primaryColor', color)}
                    style={{
                      width: '36px', height: '36px', borderRadius: '50%', background: color, cursor: 'pointer',
                      border: merged.primaryColor === color ? '3px solid #1f2937' : '2px solid #e5e7eb',
                      boxShadow: merged.primaryColor === color ? '0 0 0 2px #fff, 0 0 0 4px #1f2937' : 'none',
                    }} />
                ))}
                <input type="color" value={merged.primaryColor || '#be185d'} onChange={e => updateData('primaryColor', e.target.value)}
                  style={{ width: '36px', height: '36px', borderRadius: '50%', border: '2px solid #e5e7eb', cursor: 'pointer', padding: 0 }} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Font</label>
              <select value={merged.fontFamily || 'Georgia, serif'} onChange={e => updateData('fontFamily', e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                <option value="Georgia, serif">Georgia (Serif)</option>
                <option value="'Segoe UI', sans-serif">Segoe UI (Sans-serif)</option>
                <option value="'Palatino Linotype', serif">Palatino (Elegant)</option>
                <option value="'Trebuchet MS', sans-serif">Trebuchet (Modern)</option>
                <option value="monospace">Monospace</option>
              </select>
            </div>
          </div>
        )}

        <div style={{ marginTop: '20px', display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary" onClick={() => onSave(template, data)}>Save Card Design</button>
          {onCancel && <button className="btn btn-outline" onClick={onCancel}>Cancel</button>}
        </div>
      </div>

      {/* Right: Live preview */}
      <div style={{ flex: '1 1 400px', minWidth: '320px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>Live Preview</div>
        <CardPreview template={template} data={data} coupleNames={coupleNames} weddingDate={weddingDate} venue={venue} />
      </div>
    </div>
  );
}

export { TEMPLATES, CardPreview };
export default InvitationCardEditor;
